import axios from 'axios';
import log from 'loglevel';

import { commitSyncedSnapshot } from './commit';
import {
  pickNextSnapshot,
  type SyncBookkeeping,
  type SyncEligibilityContext,
} from './eligibility';
import { markSnapshotBlocked } from './markBlocked';
import { snapshotToDraftData, snapshotToDraftRequestContent } from './request';
import {
  findSupersededSessionIds,
  resolveSnapshotLineageId,
} from './superseded';
import { ComposeSnapshotSyncBlockedReason } from '../../enums/composeSnapshotSyncBlockedReason';
import { buildDraftRequest } from '../../hooks/UserCommunications/draft/draftRequest';
import { resolveDraftAccount } from '../../hooks/UserCommunications/draft/resolveEmailAccount';
import { isDraftNotFoundError } from '../../hooks/UserCommunications/draft/useDeleteDraft';
import type { ComposeSnapshot } from '../../models/composeSnapshot';
import {
  DraftVersionConflictError,
  RequestPayloadTooLargeError,
} from '../../services/unifiedEmailAction.service';
import {
  beginSnapshotSync,
  useComposeSnapshotIndexStore,
} from '../../store/ComposeSnapshotIndex';
import { useIntegrationsStore } from '../../store/Inbox/Integrations';
import {
  deleteComposeSnapshot,
  deleteComposeSnapshotsByDraftId,
  listDirtyComposeSnapshots,
} from '../../store/utils/composeSnapshotCache';
import {
  COMPOSE_SNAPSHOT_SYNC_CREATE_TIMEOUT_COOLDOWN_MS,
  COMPOSE_SNAPSHOT_SYNC_LOCK_NAME,
} from '../constants/drafts';
import { sendDraftWrite } from '../draftWrites';

const LOG_MESSAGES = {
  NO_LOCKS: 'navigator.locks is unavailable, compose snapshots are not synced',
  COMMIT_FAILED: 'Compose snapshot synced but the store commit failed',
  SYNC_FAILED: 'Compose snapshot sync failed',
} as const;

const HTTP_STATUS_NOT_FOUND = 404;
const GATEWAY_TIMEOUT_STATUSES: readonly number[] = [502, 503, 504];
const TIMEOUT_ERROR_CODES: readonly string[] = ['ECONNABORTED', 'ETIMEDOUT'];

export enum SyncOutcomeKind {
  SUCCESS = 'SUCCESS',
  BLOCKED = 'BLOCKED',
  DRAFT_GONE = 'DRAFT_GONE',
  RETRY = 'RETRY',
}

export type SyncOutcome =
  | { kind: SyncOutcomeKind.SUCCESS; draftId: string }
  | { kind: SyncOutcomeKind.DRAFT_GONE }
  | {
      kind: SyncOutcomeKind.BLOCKED;
      reason: ComposeSnapshotSyncBlockedReason;
      conflictDraftVersion?: string;
    }
  | { kind: SyncOutcomeKind.RETRY; isTimeout: boolean };

const RETRY_OUTCOME: SyncOutcome = {
  kind: SyncOutcomeKind.RETRY,
  isTimeout: false,
};

export interface SyncRuntime extends SyncBookkeeping {
  attemptsBySessionId: Map<string, number>;
  retryNotBeforeBySessionId: Map<string, number>;
  hasLoggedNoLocks: boolean;
}

export const createSyncRuntime = (): SyncRuntime => ({
  attemptsBySessionId: new Map(),
  retryNotBeforeBySessionId: new Map(),
  hasLoggedNoLocks: false,
});

export const resetSyncAttempts = (runtime: SyncRuntime): void =>
  runtime.attemptsBySessionId.clear();

const isNotFound = (error: unknown): boolean => {
  if (
    axios.isAxiosError(error) &&
    error.response?.status === HTTP_STATUS_NOT_FOUND
  ) {
    return true;
  }
  try {
    return isDraftNotFoundError(error);
  } catch {
    return false;
  }
};

const isTimeout = (error: unknown): boolean =>
  axios.isAxiosError(error) &&
  (TIMEOUT_ERROR_CODES.includes(error.code ?? '') ||
    GATEWAY_TIMEOUT_STATUSES.includes(error.response?.status ?? 0));

export const classifySyncError = (
  error: unknown,
  snapshot: ComposeSnapshot,
): SyncOutcome => {
  if (error instanceof DraftVersionConflictError) {
    return {
      kind: SyncOutcomeKind.BLOCKED,
      reason: ComposeSnapshotSyncBlockedReason.CONFLICT,
      conflictDraftVersion: error.currentDraftVersion,
    };
  }
  if (error instanceof RequestPayloadTooLargeError) {
    return {
      kind: SyncOutcomeKind.BLOCKED,
      reason: ComposeSnapshotSyncBlockedReason.PAYLOAD_TOO_LARGE,
    };
  }
  if (snapshot.draftId && isNotFound(error)) {
    return { kind: SyncOutcomeKind.DRAFT_GONE };
  }
  return { kind: SyncOutcomeKind.RETRY, isTimeout: isTimeout(error) };
};

const commitSafely = (input: Parameters<typeof commitSyncedSnapshot>[0]) => {
  try {
    commitSyncedSnapshot(input);
    useComposeSnapshotIndexStore
      .getState()
      .removeEntry(input.snapshot.sessionId);
  } catch (error) {
    log.warn(LOG_MESSAGES.COMMIT_FAILED, error);
  }
};

const syncSnapshot = async (
  snapshot: ComposeSnapshot,
): Promise<SyncOutcome> => {
  const { currentIntegrationAccount, integrationAccounts } =
    useIntegrationsStore.getState();
  if (!currentIntegrationAccount) {
    return RETRY_OUTCOME;
  }
  const account = resolveDraftAccount(
    snapshot.fromEmail,
    currentIntegrationAccount,
    integrationAccounts ?? [],
    { strict: Boolean(snapshot.draftId) },
  );
  if (!account) {
    return RETRY_OUTCOME;
  }
  try {
    const data = snapshotToDraftData(snapshot);
    const request = buildDraftRequest(
      snapshotToDraftRequestContent(snapshot, data),
      account,
    );
    const { message: response } = await sendDraftWrite(request, {
      lineageId: resolveSnapshotLineageId(snapshot),
      isOwnVersionTrusted: false,
    });
    if (!response?.messageId) {
      return RETRY_OUTCOME;
    }
    commitSafely({ snapshot, data, response, account });
    return { kind: SyncOutcomeKind.SUCCESS, draftId: response.messageId };
  } catch (error) {
    log.warn(LOG_MESSAGES.SYNC_FAILED, error);
    return classifySyncError(error, snapshot);
  }
};

const removeSyncedSnapshot = async (
  snapshot: ComposeSnapshot,
  draftId: string,
): Promise<void> => {
  await deleteComposeSnapshot(snapshot.userId, snapshot.sessionId);
  // Only a create yields an id nothing else could hold; an update's id is shared with newer snapshots.
  if (!snapshot.draftId) {
    await deleteComposeSnapshotsByDraftId(
      snapshot.userId,
      draftId,
      Object.keys(useComposeSnapshotIndexStore.getState().liveSessionIds),
    );
  }
};

const applyOutcome = async (
  snapshot: ComposeSnapshot,
  outcome: SyncOutcome,
  runtime: SyncRuntime,
): Promise<void> => {
  if (outcome.kind === SyncOutcomeKind.SUCCESS) {
    runtime.attemptsBySessionId.delete(snapshot.sessionId);
    useComposeSnapshotIndexStore
      .getState()
      .recordSyncedDraft(snapshot.sessionId, outcome.draftId);
    await removeSyncedSnapshot(snapshot, outcome.draftId);
    return;
  }
  if (outcome.kind === SyncOutcomeKind.DRAFT_GONE) {
    // A compose may have claimed the row while the request ran; it shows the deleted pill itself.
    if (
      !useComposeSnapshotIndexStore.getState().liveSessionIds[
        snapshot.sessionId
      ]
    ) {
      await deleteComposeSnapshot(snapshot.userId, snapshot.sessionId);
    }
    return;
  }
  if (outcome.kind === SyncOutcomeKind.BLOCKED) {
    await markSnapshotBlocked(
      snapshot,
      outcome.reason,
      outcome.conflictDraftVersion,
    );
    return;
  }
  if (outcome.isTimeout && !snapshot.draftId) {
    runtime.retryNotBeforeBySessionId.set(
      snapshot.sessionId,
      Date.now() + COMPOSE_SNAPSHOT_SYNC_CREATE_TIMEOUT_COOLDOWN_MS,
    );
  }
};

// Returns false when the pass should stop (a retryable failure).
const syncOne = async (
  snapshot: ComposeSnapshot,
  runtime: SyncRuntime,
): Promise<boolean> => {
  const { sessionId } = snapshot;
  // A compose may have claimed the row after the queue was built; no await separates this from the request.
  if (useComposeSnapshotIndexStore.getState().liveSessionIds[sessionId]) {
    return true;
  }
  runtime.attemptsBySessionId.set(
    sessionId,
    (runtime.attemptsBySessionId.get(sessionId) ?? 0) + 1,
  );
  const settle = beginSnapshotSync(sessionId);
  let outcome: SyncOutcome = RETRY_OUTCOME;
  try {
    outcome = await syncSnapshot(snapshot);
    await applyOutcome(snapshot, outcome, runtime);
  } finally {
    settle({
      draftId:
        outcome.kind === SyncOutcomeKind.SUCCESS ? outcome.draftId : undefined,
    });
  }
  return outcome.kind !== SyncOutcomeKind.RETRY;
};

const buildContext = (
  runtime: SyncRuntime,
  now: number,
): SyncEligibilityContext => ({
  attemptsBySessionId: runtime.attemptsBySessionId,
  retryNotBeforeBySessionId: runtime.retryNotBeforeBySessionId,
  liveSessionIds: useComposeSnapshotIndexStore.getState().liveSessionIds,
  now,
});

// Ignores time gates (grace, cooldown) so the backoff timer keeps ticking until they open.
export const hasPendingSyncWork = async (
  userId: string,
  runtime: SyncRuntime,
): Promise<boolean> =>
  pickNextSnapshot(
    await listDirtyComposeSnapshots(userId),
    buildContext(runtime, Number.POSITIVE_INFINITY),
  ) !== undefined;

// Returns true when a retryable failure ended the pass.
const dropSupersededSnapshots = async (
  snapshots: ComposeSnapshot[],
): Promise<void> => {
  const { liveSessionIds } = useComposeSnapshotIndexStore.getState();
  for (const sessionId of findSupersededSessionIds(snapshots, liveSessionIds)) {
    const snapshot = snapshots.find((item) => item.sessionId === sessionId);
    if (snapshot) {
      await deleteComposeSnapshot(snapshot.userId, sessionId);
      useComposeSnapshotIndexStore.getState().removeEntry(sessionId);
    }
  }
};

const drain = async (
  userId: string,
  runtime: SyncRuntime,
): Promise<boolean> => {
  while (navigator.onLine) {
    const snapshots = await listDirtyComposeSnapshots(userId);
    await dropSupersededSnapshots(snapshots);
    const next = pickNextSnapshot(snapshots, buildContext(runtime, Date.now()));
    if (!next) {
      return false;
    }
    if (!(await syncOne(next, runtime))) {
      return true;
    }
  }
  return false;
};

export const runComposeSnapshotSyncPass = async (
  userId: string,
  runtime: SyncRuntime,
): Promise<boolean> => {
  if (!navigator.onLine) {
    return false;
  }
  if (!navigator.locks) {
    if (!runtime.hasLoggedNoLocks) {
      runtime.hasLoggedNoLocks = true;
      log.warn(LOG_MESSAGES.NO_LOCKS);
    }
    return false;
  }
  return navigator.locks.request(
    COMPOSE_SNAPSHOT_SYNC_LOCK_NAME,
    { ifAvailable: true },
    async (lock) => (lock ? drain(userId, runtime) : false),
  );
};
